import React, { useContext } from "react";
import { CombinedContext } from "../../../context/combinedContext";
import translations from "../../../translations/translations";
import { CalendarClock, ArrowUp, Mail } from "lucide-react";
import useReveal from "../../common/useReveal";
import "./legalDoc.css";

/* Headings arrive pre-numbered as "1. Foo" or "1) Foo". Split the number out
   so it can be set as a badge and the text can stand on its own in the index. */
const splitHeading = (heading = "") => {
	const match = heading.match(/^(\d+)\s*[.)]\s*(.*)$/);
	return match ? { num: match[1], text: match[2] } : { num: null, text: heading };
};

const slugify = (id, i) => id || `section-${i + 1}`;

/**
 * Shared shell for the long-form legal pages (privacy, terms, refund).
 * Pages hand it a `sections` array; it renders the header, the sticky index
 * and the body so all three documents stay identical in look and behaviour.
 *
 * Section shape — every field optional except `heading`:
 *   { id, heading, paragraphs: [], list: [], groups: [{ subheading, list }], closing: [], footnote }
 * `paragraphs` lead the section; `closing` follows the list or groups.
 */
const LegalDoc = ({ title, lastUpdatedLabel, lastUpdatedValue, intro = [], sections = [] }) => {
	const { language } = useContext(CombinedContext);
	const rootRef = useReveal();
	const t = translations[language].footer.legal;

	return (
		<div className="sf-lg-root" ref={rootRef}>

			{/* ── HEADER ── */}
			<header className="sf-lg-hero">
				<div className="sf-lg-hero-grid" aria-hidden="true"></div>

				<div className="sf-lg-hero-inner">
					<span className="sf-lg-hero-eyebrow">{t.eyebrow}</span>
					<h1>{title}</h1>
					<span className="sf-lg-updated">
						<CalendarClock size={15} strokeWidth={1.9} />
						{lastUpdatedLabel}: <strong>{lastUpdatedValue}</strong>
					</span>
				</div>
			</header>

			<div className="sf-lg-page">
				{/* ── INDEX ── */}
				<aside className="sf-lg-toc">
					<div className="sf-lg-toc-inner">
						<h2>{t.contents}</h2>
						<ol>
							{sections.map((section, i) => {
								const { num, text } = splitHeading(section.heading);
								return (
									<li key={slugify(section.id, i)}>
										<a href={`#${slugify(section.id, i)}`}>
											<span className="sf-lg-toc-num">{num || i + 1}</span>
											{text}
										</a>
									</li>
								);
							})}
						</ol>
					</div>
				</aside>

				{/* ── DOCUMENT ── */}
				<main className="sf-lg-body">
					{intro.length > 0 && (
						<div className="sf-lg-intro" data-reveal>
							{intro.map((paragraph, i) => (
								<p key={i}>{paragraph}</p>
							))}
						</div>
					)}

					{sections.map((section, i) => {
						const { num, text } = splitHeading(section.heading);
						return (
							<section
								id={slugify(section.id, i)}
								className="sf-lg-section"
								key={slugify(section.id, i)}
								data-reveal
							>
								<h2>
									<span className="sf-lg-num">{num || i + 1}</span>
									{text}
								</h2>

								{section.paragraphs?.map((paragraph, j) => (
									<p key={j}>{paragraph}</p>
								))}

								{section.list && (
									<ul className="sf-lg-list">
										{section.list.map((item, j) => (
											<li key={j}>{item}</li>
										))}
									</ul>
								)}

								{section.groups?.map((group, j) => (
									<div className="sf-lg-group" key={j}>
										<h3>{group.subheading}</h3>
										<ul className="sf-lg-list">
											{group.list.map((item, k) => (
												<li key={k}>{item}</li>
											))}
										</ul>
									</div>
								))}

								{section.closing?.map((paragraph, j) => (
									<p key={j}>{paragraph}</p>
								))}

								{section.footnote && (
									<p className="sf-lg-footnote">
										<Mail size={15} strokeWidth={1.9} />
										<span>{section.footnote}</span>
									</p>
								)}
							</section>
						);
					})}

					<button
						type="button"
						className="sf-lg-top"
						onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
					>
						<ArrowUp size={15} strokeWidth={2} />
						{t.backToTop}
					</button>
				</main>
			</div>
		</div>
	);
};

export default LegalDoc;
